import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@components/Button/Button";
import { BinIcon } from "@components/icons/common";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveMessageMutation } from "@services/messages/mutations";
import { type IMessage } from "@services/messages/types";

interface IRemoveMessageModalProps extends IUseModalProps {
  messageId: IMessage["messageId"];
}

export const RemoveMessageModal: React.FC<IRemoveMessageModalProps> = (
  props,
) => {
  const { messageId, ...modalProps } = props;
  const modal = useModal(modalProps);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutateAsync: removeMessage, isPending } = useRemoveMessageMutation();

  const submitRemove = () => {
    removeMessage(messageId)
      .then(() => {
        navigate("/messages");
        modal.close();
      })
      .catch(() => {
        toast.error(t("toast.error"));
      });
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BinIcon, color: "red" }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("removeMessageModal.actions.cancel")}
          </Button>
          <Button
            onClick={submitRemove}
            className="w-full"
            isLoading={isPending}
          >
            {t("removeMessageModal.actions.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("removeMessageModal.title")}
      subtitle={t("removeMessageModal.subtitle")}
      className="md:max-w-xl"
    />
  );
};
